/**
 * ErrorSurface — Anaka's single parametric error / 404 surface.
 * Voice: calm & reassuring. Ported from the Claude Design handoff
 * (`404 Page.html` · `screens/not-found.jsx`).
 *
 * Layout (top → bottom):
 *   top chrome   — the real nav (signed in, injected via `nav`) OR a minimal
 *                  wordmark + system-status pill (guest) OR nothing
 *   banner       — system-status strip, only when a service isn't operational
 *   stage        — the 4·A·4 motif, the headline, Anaka's message card, actions
 *
 * The final design (design chat) dropped the eyebrow tag, the mono error-
 * reference footer, and the banner's "live status" link, so none are rendered.
 *
 * This component is intentionally free of app dependencies (no auth / data /
 * router) — pages compose it via `ErrorScenarioView`, passing the nav node and
 * action handlers in. That keeps the surface trivially testable and reusable.
 */
import { type ReactNode } from 'react';
import {
  AlertTriangle,
  ArrowLeft,
  Home,
  LogIn,
  RefreshCw,
  type LucideIcon,
} from 'lucide-react';

import './error-surface.css';

/** Served from `public/assets/` at the app root. */
const ANAKA_ICON = '/assets/anaka_icon.png';

export type ErrorActionIcon = 'back' | 'home' | 'login' | 'refresh';

const ACTION_ICONS: Record<ErrorActionIcon, LucideIcon> = {
  back: ArrowLeft,
  home: Home,
  login: LogIn,
  refresh: RefreshCw,
};

export interface ErrorSurfaceAction {
  label: string;
  kind: 'primary' | 'ghost';
  icon?: ErrorActionIcon;
  /** Path href for navigations (e.g. `/`, `/login`). Renders an anchor. */
  href?: string;
  /** Click handler for non-navigational actions (e.g. Back). Renders a button. */
  onClick?: () => void;
}

export type ErrorSurfaceTopbar = 'nav' | 'wordmark' | 'none';
export type ErrorStatusLevel = 'operational' | 'degraded' | 'down';

export interface ErrorSurfaceBanner {
  level: 'degraded' | 'down';
  text: string;
}

export interface ErrorSurfaceProps {
  /** Numeral for the motif + a11y label; motif shows first + last character. */
  code?: string;
  headline: string;
  message: ReactNode;
  /** Default `wordmark`. `nav` renders the injected `nav` node (falls back to wordmark). */
  topbar?: ErrorSurfaceTopbar;
  /** The real authenticated nav, rendered when `topbar === 'nav'`. */
  nav?: ReactNode;
  /** Status pill shown in wordmark chrome. */
  status?: ErrorStatusLevel;
  hideStatus?: boolean;
  /** System-status strip under the chrome (not-operational scenarios). */
  banner?: ErrorSurfaceBanner | null;
  /** Action buttons. Defaults to the two static choices: Back / Home. */
  actions?: ErrorSurfaceAction[];
  motif?: 'standard' | 'broken';
  /** Optional adornment in the message-card meta row (e.g. the voice control). */
  messageAside?: ReactNode;
}

const STATUS_LABEL: Record<ErrorStatusLevel, string> = {
  operational: 'All systems operational',
  degraded: 'Some services degraded',
  down: 'Service interruption',
};

const DEFAULT_ACTIONS: ErrorSurfaceAction[] = [
  { label: 'Back', kind: 'ghost', icon: 'back', onClick: () => window.history.back() },
  { label: 'Home', kind: 'primary', icon: 'home', href: '/' },
];

function ActionButton({ action }: { action: ErrorSurfaceAction }) {
  const Icon = action.icon ? ACTION_ICONS[action.icon] : null;
  const className = `es-btn ${action.kind === 'primary' ? 'es-btn-primary' : 'es-btn-ghost'}`;
  const inner = (
    <>
      {Icon ? <Icon aria-hidden="true" /> : null}
      {action.label}
    </>
  );

  if (action.href) {
    return (
      <a className={className} href={action.href}>
        {inner}
      </a>
    );
  }
  return (
    <button type="button" className={className} onClick={action.onClick}>
      {inner}
    </button>
  );
}

export function ErrorSurface({
  code = '404',
  headline,
  message,
  topbar = 'wordmark',
  nav,
  status = 'operational',
  hideStatus = false,
  banner = null,
  actions,
  motif = 'standard',
  messageAside,
}: ErrorSurfaceProps) {
  const acts = actions ?? DEFAULT_ACTIONS;
  const showNav = topbar === 'nav' && Boolean(nav);
  const showWordmark = topbar === 'wordmark' || (topbar === 'nav' && !nav);

  return (
    <div className="es-root" data-testid="error-surface" data-code={code}>
      <div className="es-glow" aria-hidden="true" />

      {showNav ? nav : null}
      {showWordmark ? (
        <header className="es-top" data-testid="error-wordmark">
          <div className="es-brand">
            <img src={ANAKA_ICON} alt="" />
            <span>Anaka</span>
          </div>
          {hideStatus ? null : (
            <div className={`es-status is-${status}`} data-testid="error-status">
              {STATUS_LABEL[status]}
            </div>
          )}
        </header>
      ) : null}

      {banner ? (
        <div className={`es-banner is-${banner.level}`} role="status" data-testid="error-banner">
          <span className="es-banner-ico">
            <AlertTriangle size={15} aria-hidden="true" />
          </span>
          <span className="es-banner-text">{banner.text}</span>
        </div>
      ) : null}

      <main className="es-stage">
        {/* motif: the Anaka glyph above the complete status code (e.g. 404, 503) */}
        <div
          className={`es-numeral ${motif === 'broken' ? 'is-broken' : ''}`}
          aria-hidden="true"
        >
          <span className="es-glyph">
            <img src={ANAKA_ICON} alt="" />
            {motif === 'broken' ? <i className="es-crack" /> : null}
          </span>
          <span className="es-code">{code}</span>
        </div>

        <h1 className="es-headline">{headline}</h1>

        {/* Anaka explains, in plain language — styled like one of her messages */}
        <div className="es-msg">
          <div className="es-msg-av">
            <img src={ANAKA_ICON} alt="Anaka" />
          </div>
          <div className="es-msg-body">
            <div className="es-msg-meta">
              <strong>Anaka</strong>
              <span>just now</span>
              {messageAside ? <span className="es-msg-aside">{messageAside}</span> : null}
            </div>
            <p className="es-msg-text">{message}</p>
          </div>
        </div>

        <div className="es-actions">
          {acts.map((action, i) => (
            <ActionButton key={`${action.label}-${i}`} action={action} />
          ))}
        </div>
      </main>
    </div>
  );
}

export default ErrorSurface;
